import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./OnboardingFormConfig.module.css";
import { Row } from "react-bootstrap";
import CygnusForm from "../../../Reusables/components/CygnusForm/CygnusForm";
import CygnusFormBuilder from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder";
import {
  InputStructureProps,
  createDefaultInitialData,
} from "../../../Reusables/components/CygnusFormBuilder/CygnusFormBuilder.utils";
import {
  fetchGroupedTemplates,
  fetchFirmTemplateFields,
  saveFormData,
  fetchFormPurposeTypes,
  fetchPurposeOptions, 
} from "./OnboardingFormConfig.service";
import { FieldProps } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import {
  onboardingMetaData,
  optionsConverter,
} from "./OnboardingFormConfig.utils";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { AlertModal } from "../../../Reusables/components/AlertModal/AlertModal";
import { NON_MAPPED_FIELD_TYPES } from "../../../utils/constants";
import { useGlobalStore } from "../../store/globalStore";

/**
 * `OnboardingFormConfig` manages metadata (name, purpose, template, type),
 * form building with CygnusFormBuilder, and previewing via CygnusForm.
 *
 * @returns {JSX.Element}
 */
const OnboardingFormConfig: React.FC<{
  onboarding_form_config_option: {
    lineOfService: string;
    lineOfServiceCode: string;
    createform_option: {
      id: string;
      templateid: string;
      form_name: string;
      form_purpose: string;
      line_of_service: string;
      initial_data: InputStructureProps;
      is_final: string;
    };
  };
}> = (config_props) => {
  const { purposeOptions } = useGlobalStore();

  const configOption = useMemo(
    () => config_props?.onboarding_form_config_option || {},
    [config_props]
  );
  const prefill = useMemo(
    () => configOption?.createform_option || {},
    [configOption]
  );

  const [initialData, setInitialData] = useState<InputStructureProps>(
    createDefaultInitialData()
  );
  const [selectedFieldOptions, setSelectedFieldOptions] = useState<
    {
      value: string;
      option: [];
      label: string;
      type: string;
      id: string;
      code: string;
    }[]
  >([]);
  const [groupedTemplates, setGroupedTemplates] = useState<{
    [key: string]: any[];
  }>({});
  const [metadataFields, setMetadataFields] =
    useState<FieldProps[]>(onboardingMetaData);
  const [isMetadataValid, setIsMetadataValid] = useState<boolean>(false);
  const [update, setUpdate] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState<string>("");
  
  const hasPrefilledRef = useRef(false);
  const isPrefillingRef = useRef(false);

  // Track loading states
  const [lobOptionsLoaded, setLobOptionsLoaded] = useState(false);
  const [formTypesLoaded, setFormTypesLoaded] = useState(false);

  // Populate Line of Service options - try from store first, then fetch if empty
  useEffect(() => {
    const loadLobOptions = async () => {
      let lobOptions: { label: string; value: string; id: string }[] = [];

      // Try from global store first
      if (purposeOptions && purposeOptions.length > 0) {
        // console.log("Using purposeOptions from global store:", purposeOptions);
        lobOptions = purposeOptions.map((opt: any) => ({
          label: opt.label,
          value: opt.value,
          id: opt.code,
        }));
      } else {
        // Fallback: Fetch directly from API
        // console.log("purposeOptions empty, fetching from API...");
        try {
          const fetchedOptions = await fetchPurposeOptions();
          // console.log("Fetched purpose options:", fetchedOptions);
          lobOptions = fetchedOptions.map((opt: any) => ({
            label: opt.label,
            value: opt.value,
            id: opt.code,
          }));
        } catch (error) {
          console.error("Failed to fetch purpose options:", error);
        }
      }

      if (lobOptions.length > 0) {
        // console.log("Setting LOB options:", lobOptions);
        setMetadataFields((prev) =>
          prev.map((field) =>
            field.id === "lineOfService"
              ? { ...field, options: lobOptions }
              : field
          )
        );
        setLobOptionsLoaded(true);
      }
    };

    loadLobOptions();
  }, [purposeOptions]);

  // Prefill - now waits for ALL dependencies
  useEffect(() => {
    const hasPrefillData = prefill && Object.keys(prefill).length > 0;
    const allLoaded = 
      lobOptionsLoaded && 
      formTypesLoaded && 
      Object.keys(groupedTemplates).length > 0;
    // console.log("Prefill check:", { hasPrefillData, lobOptionsLoaded, formTypesLoaded, templatesLoaded: Object.keys(groupedTemplates).length > 0 });
    if (!hasPrefillData || !allLoaded || hasPrefilledRef.current) return;

    hasPrefilledRef.current = true;
    isPrefillingRef.current = true;
    // console.log("Prefilling form with:", prefill);
    setMetadataFields((prevFields) =>
      prevFields.map((field) => {
        switch (field.id) {
          case "formName":
            return { ...field, value: prefill.form_name };
          case "formPurpose":
            return { ...field, value: prefill.form_purpose };
          case "formTemplate":
            const options =
              groupedTemplates?.[prefill.form_purpose]?.map((t: any) => ({
                id: t.id,
                label: t.FormName,
                value: t.FormName,
              })) || [];
            return {
              ...field,
              options,
              value: prefill.templateid || "",
            };
          case "formType":
            return { ...field, value: prefill.form_purpose || "" };
          case "lineOfService": {
            const lobOpt = field.options?.find((opt: any) => opt.id === prefill.line_of_service);
            return { ...field, value: lobOpt?.value || prefill.line_of_service || "" };
          }
          case "isFinal":
            return {
              ...field,
              value: prefill.is_final ?? false,
              label: prefill.is_final ? "Final" : "Draft",
            };
          default:
            return field;
        }
      })
    );

    if (prefill.initial_data) {
      setInitialData(prefill.initial_data);
      setUpdate(true);
    }

    setTimeout(() => {
      isPrefillingRef.current = false;
    }, 100);
    
  }, [prefill, groupedTemplates, lobOptionsLoaded, formTypesLoaded]);

  // Dynamically update dependent fields
  const handleMetadataChange = (updatedFields: FieldProps[]) => {
    if (isPrefillingRef.current) {
      // console.log("Skipping handleMetadataChange - prefilling in progress");
      return;
    }

    const lobField = updatedFields.find((f) => f.id === "lineOfService");
    console.log("handleMetadataChange - LOB field:", lobField);

    const finalField = updatedFields.find((f) => f.id === "isFinal");
    if (finalField) {
      finalField.label = finalField.value ? "Final" : "Draft";
    }

    const formTypeField = updatedFields.find((f) => f.id === "formType");
    const purpose = formTypeField?.options?.find(
      (opt: { value: string }) => opt.value === formTypeField?.value
    )?.label || "";

    if (purpose && groupedTemplates[purpose]) {
      const templates = groupedTemplates[purpose];
      const templateField = updatedFields.find((f) => f.id === "formTemplate");
      if (templateField) {
        templateField.options = templates.map((t: any) => ({
          id: t.id,
          label: t.FormName,
          value: t.FormName,
        }));
        const prevFormType = metadataFields.find((f) => f.id === "formType")?.value;
        if (prevFormType !== formTypeField?.value) {
          templateField.value = "";
        }
      }
    }

    setMetadataFields([...updatedFields]);
  };

  useEffect(() => {
    fetchGroupedTemplates().then((templates) => setGroupedTemplates(templates));

    fetchFormPurposeTypes().then((purposeList) => {
      const formTypes = purposeList.map((m: any) => ({
        label: m.ReferenceDataValue,
        value: m.ReferenceDataCode,
      }));

      setMetadataFields((prev) =>
        prev.map((field) =>
          field.id === "formType"
            ? { ...field, options: formTypes }
            : field
        )
      );
      setFormTypesLoaded(true);
    });

    const loadSelectedFields = async () => {
      const rawFields = await fetchFirmTemplateFields();
      const parsedFields = await optionsConverter(rawFields);
      console.log("PARSED FIELDS: ", parsedFields);
      setSelectedFieldOptions(parsedFields);
    };
    loadSelectedFields();
  }, []);

  const validateForm = () => {
    console.log("VALIDATE FIELDS: ", initialData.fields);
    const missingLabels = initialData.fields
      .filter(
        (item) =>
          !NON_MAPPED_FIELD_TYPES.includes(item.type) &&
          (!item.allfields_key || item.allfields_key.trim() === "")
      )
      .map((item) => item.label || "(no label)");

    if (missingLabels.length > 0) {
      setMessage(
        `There is an error in the form. Please select a "Field" for the following inputs: ${missingLabels.join(
          ", "
        )}`
      );
      setShowAlert(true);
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    const fieldMap: Record<string, any> = {};
    metadataFields.forEach((f) => (fieldMap[f.id] = f.value));
 
    const payload = update
      ? {
          FirmFormId: prefill.id,
          FormName: fieldMap["formName"],
          FormDescription: "",
          FormTypeCode: fieldMap["formType"],
          LineOfServiceCode: purposeOptions.find((p) => p.value === fieldMap["lineOfService"])?.code || "",
          Active: "Y",
          Status: fieldMap["isFinal"] ? "Active" : "Inactive",
          InputStructure: initialData,
        }
      : {
          FormName: fieldMap["formName"],
          FormDescription: "",
          FormTypeCode: fieldMap["formType"],
          LineOfServiceCode: purposeOptions.find((p) => p.value === fieldMap["lineOfService"])?.code || "",
          Active: "Y",
          Status: fieldMap["isFinal"] ? "Active" : "Inactive",
          InputStructure: initialData,
        };
        // console.log("Payload for saving form:", payload);
    const res = await saveFormData(payload, update);
    if (res?.success) {
      if (res.data?.DeactivatedFormName) {
        alert(
          `Form saved and activated! Previous active form "${res.data.DeactivatedFormName}" was deactivated.`
        );
      } else {
        alert("Form saved successfully!");
      }
    }
  };

  return (
    <>
      <AlertModal
        show={showAlert}
        onHide={() => setShowAlert(false)}
        title="Error in the form"
        message={message}
        type="warning"
        listItems={[]}
        footerButtons={[
          { label: "Cancel", onClick: () => setShowAlert(false) },
        ]}
      />
      <Row className={`no-gutter ${styles.formMetadataWrapper}`}>
        <p className="display-6"> Cygnus Intake Form Builder</p>
      </Row>
      <Row className={`no-gutter ${styles.formMetadataWrapper}`}>
        <div className={styles.formMetadataContainer}>
          <CygnusForm
            key={`form-${lobOptionsLoaded}-${formTypesLoaded}`}
            formId="cygnus-intake-form"
            fields={metadataFields}
            onFieldsChange={(fields) => {
              console.log("fields from CygnusForm: ", fields);
              handleMetadataChange(fields);
            }}
            onValidationChange={(flag) => {
              setIsMetadataValid(flag);
            }}
          />
        </div>
        <div className={styles.onboardingFormSaveWrapper}>
          <CustomButton
            disabled={!isMetadataValid}
            onclick={validateForm}
            content={"Save"}
          />
        </div>
      </Row>
      <Row className="no-gutters">
        <CygnusFormBuilder
          initialData={initialData}
          updateInitialData={setInitialData}
          fieldOptions={selectedFieldOptions}
          templateStatus={
            metadataFields.find((f) => f.id === "isFinal")?.value
              ? "Final"
              : "Draft"
          }
        />
      </Row>
    </>
  );
};

export default OnboardingFormConfig;